import React from "react";
import {
  Bike,
  Footprints,
  PersonStanding,
  Sailboat,
  StepForward,
  StretchHorizontal,
} from "lucide-react";
import { EQUIPMENT } from "../../utils/ftms";

const ICONS = {
  Bike,
  Sailboat,
  Footprints,
  PersonStanding,
  StepForward,
  StretchHorizontal,
};

const EquipmentIcon = ({ type, className }) => {
  const name = EQUIPMENT[type]?.icon || "Bike";
  const Cmp = ICONS[name] || Bike;
  return <Cmp className={className} />;
};

const formatMini = (device) => {
  const m = device.snapshot;
  if (!m) return "—";
  const w = m.power_watts != null ? `${Math.round(m.power_watts)} W` : "—";
  if (device.type === "rower" && m.stroke_rate_spm != null) {
    return `${w} · ${Math.round(m.stroke_rate_spm)} spm`;
  }
  if (device.type === "indoor_bike" && m.cadence_rpm != null) {
    return `${w} · ${Math.round(m.cadence_rpm)} rpm`;
  }
  return w;
};

/**
 * Pill row for switching the active metric stream mid-workout. Renders only
 * when ≥ 2 devices are connected — otherwise there's nothing to switch
 * between and we save the screen real estate.
 *
 * A pill marked `reconnecting` shows a pulsing dot + a contextual hint
 * (rower: "wake it up") so the user knows the device is asleep, not the app.
 */
const QuickSwitch = ({ devices, activeDeviceId, onSelect, currentPhaseId }) => {
  if (!devices || devices.length < 2) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto">
      {devices.map((d) => {
        const isActive = d.id === activeDeviceId;
        const isReconnecting = d.status === "reconnecting";
        const wakeHint = isReconnecting && d.type === "rower";
        return (
          <button
            key={d.id}
            type="button"
            onClick={() => onSelect(d.id, { phaseId: currentPhaseId })}
            aria-pressed={isActive}
            className={[
              "flex items-center gap-2 rounded-full px-3 py-1.5 text-xs whitespace-nowrap",
              "border transition-colors",
              isActive
                ? "bg-yellow-400/20 border-yellow-400 text-yellow-100"
                : "bg-white/[0.04] border-white/15 text-white/70 hover:text-white hover:border-white/30",
            ].join(" ")}
          >
            <EquipmentIcon type={d.type} className="w-3.5 h-3.5" />
            <span className="font-medium truncate max-w-[7rem]">{d.name}</span>
            <span className="font-mono tabular-nums text-white/60">
              {formatMini(d)}
            </span>
            {isReconnecting && (
              <span className="flex items-center gap-1 text-yellow-400/80">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
                {wakeHint ? "wake it up" : "reconnect"}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default QuickSwitch;
